import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCcw } from "lucide-react";
import { EquipmentSection } from "@/components/EquipmentSection";

const EMPTY_RENTAL = {
    equipment_type: "reefer",
    equipment_type_other: "",
    unit_number: "",
    make: "",
    model: "",
    year: "",
    vin: "",
    plate: "",
    start_date: "",
    end_date: "",
    daily_rate: "",
    monthly_rate: "",
    minimum_term_months: "",
    billing_period: "",
    deposit: "",
    // Phase H.7 — equipment replacement value in whole USD; required for
    // rental_agreement contracts. Stored as a string in the form (so the
    // currency-masked input can stay controlled) and coerced to int at submit.
    value: "",
    notes: "",
    // Phase AL1 — Alvys provenance + extra equipment metadata. Set ONLY
    // when the user clicks "Use this data" on the lookup banner; the
    // mere act of typing a unit number does not stamp these.
    equipment_size: "",
    alvys_trailer_id: "",
    alvys_synced_at: "",
};

// Phase H.7 — strip non-digits, return an int (or "" when empty/zero).
const _parseValueInput = (raw) => {
    const digits = String(raw || "").replace(/[^0-9]/g, "");
    return digits === "" ? "" : String(parseInt(digits, 10));
};

const _displayValue = (raw) => {
    const digits = String(raw || "").replace(/[^0-9]/g, "");
    if (!digits) return "";
    return `$${parseInt(digits, 10).toLocaleString("en-US")}`;
};

/**
 * Phase F1 — atomic rental + contract creation. Two optional sections:
 *   • Rental Agreement template (required)
 *   • ACH Authorization template (optional bundled doc, fully wired in F2)
 *
 * Props:
 *   open, onOpenChange, customerId, customerName, onCreated(contract)
 */
export function CreateRentalContractDialog({ open, onOpenChange, customerId, customerName, onCreated, prefillFromRentalId }) {
    const [title, setTitle] = useState("");
    const [notes, setNotes] = useState("");
    const [templateId, setTemplateId] = useState("");
    const [achTemplateId, setAchTemplateId] = useState("");
    const [rental, setRental] = useState({ ...EMPTY_RENTAL });
    const [templates, setTemplates] = useState([]);
    const [busy, setBusy] = useState(false);
    // Phase TASK28E-REASSIGN-PREFILL — prefill provenance state.
    const [prefillBanner, setPrefillBanner] = useState(null); // {source_customer_name, source_status, inherited_fields[]}
    const [prefillError, setPrefillError] = useState(null);
    // Phase H.1 — surface a small note if the persisted system default points
    // to an archived template (so admin doesn't think the dropdown was just
    // forgotten).
    const [defaultArchived, setDefaultArchived] = useState(false);
    // Phase H.4 — same flag for the ACH default.
    const [achDefaultArchived, setAchDefaultArchived] = useState(false);
    // Phase H.6 — explicit "Include ACH Authorization" toggle. Replaces the
    // implicit "dropdown has a value == bundle ACH" model. Defaults ON iff a
    // system default ACH template id is configured (archived or not) — that
    // preserves the Phase H.4 experience for admins who had set a default.
    const [bundleAch, setBundleAch] = useState(false);
    // Phase H.6 — remember the system default so toggling OFF→ON can re-apply
    // it rather than replaying whatever stale selection the admin left behind.
    const [achDefaultId, setAchDefaultId] = useState("");
    const [achDefaultValid, setAchDefaultValid] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTitle(""); setNotes(""); setTemplateId(""); setAchTemplateId("");
        setRental({ ...EMPTY_RENTAL });
        setDefaultArchived(false);
        setAchDefaultArchived(false);
        setBundleAch(false);
        setAchDefaultId("");
        setAchDefaultValid(false);
        setPrefillBanner(null);
        setPrefillError(null);
        // Phase TASK28E-REASSIGN-PREFILL — when the dialog opens with a
        // source rental id, fetch the prefill payload and hydrate the
        // rental form fields. All values remain editable — this only
        // seeds the initial state so the admin can review + tweak.
        if (prefillFromRentalId) {
            (async () => {
                try {
                    const { data } = await api.get(
                        `/admin/rentals/${prefillFromRentalId}/reassign-prefill`,
                    );
                    const p = data?.prefill || {};
                    setRental((prev) => ({
                        ...prev,
                        equipment_type: p.equipment_type ?? prev.equipment_type,
                        equipment_type_other: p.equipment_type_other ?? prev.equipment_type_other,
                        unit_number: p.unit_number ?? prev.unit_number,
                        make: p.make ?? prev.make,
                        model: p.model ?? prev.model,
                        year: p.year != null ? String(p.year) : prev.year,
                        vin: p.vin ?? prev.vin,
                        plate: p.plate ?? prev.plate,
                        daily_rate: p.daily_rate != null ? String(p.daily_rate) : prev.daily_rate,
                        monthly_rate: p.monthly_rate != null ? String(p.monthly_rate) : prev.monthly_rate,
                        minimum_term_months: p.minimum_term_months != null ? String(p.minimum_term_months) : prev.minimum_term_months,
                        billing_period: p.billing_period ?? prev.billing_period,
                        deposit: p.deposit != null ? String(p.deposit) : prev.deposit,
                        value: p.value != null ? String(p.value) : prev.value,
                        equipment_size: p.equipment_size ?? prev.equipment_size,
                        notes: p.notes ?? prev.notes,
                    }));
                    setPrefillBanner({
                        source_rental_id: data.source_rental_id,
                        source_customer_name: data.source_customer_name,
                        source_status: data.source_status,
                    });
                } catch (err) {
                    setPrefillError(formatApiError(err));
                }
            })();
        }
        (async () => {
            try {
                const [rentalRes, achRes] = await Promise.all([
                    api.get("/admin/templates", { params: { limit: 100, template_type: "rental_agreement" } }),
                    api.get("/admin/templates", { params: { limit: 100, template_type: "ach_authorization" } }),
                ]);
                const merged = [...(rentalRes.data?.items || []), ...(achRes.data?.items || [])];
                setTemplates(merged);
                // Phase H2 — pre-select the system default rental-agreement template
                // if one is configured. Phase H.1 — never silently pre-select an
                // archived default; surface a small note instead so the admin
                // explicitly picks a working template.
                try {
                    const { data: settings } = await api.get("/admin/system-settings");
                    if (settings?.default_contract_template_id) {
                        if (settings.default_contract_template_archived) {
                            setDefaultArchived(true);
                        } else {
                            const ok = (rentalRes.data?.items || []).some(
                                (t) => t.id === settings.default_contract_template_id && !t.archived
                            );
                            if (ok) setTemplateId(settings.default_contract_template_id);
                        }
                    }
                    // Phase H.4 / H.6 — ACH default drives both the toggle
                    // default state and the pre-selected dropdown value.
                    if (settings?.default_ach_template_id) {
                        setAchDefaultId(settings.default_ach_template_id);
                        // Toggle defaults ON whenever a default is configured
                        // (archived or not) — admin opted into ACH bundling at
                        // the org level. If it's archived they still see the
                        // empty dropdown + amber note and must pick manually.
                        setBundleAch(true);
                        if (settings.default_ach_template_archived) {
                            setAchDefaultArchived(true);
                        } else {
                            const okAch = (achRes.data?.items || []).some(
                                (t) => t.id === settings.default_ach_template_id && !t.archived
                            );
                            if (okAch) {
                                setAchTemplateId(settings.default_ach_template_id);
                                setAchDefaultValid(true);
                            }
                        }
                    }
                } catch { /* admin (non-super) — banner shouldn't break dialog */ }
            } catch (err) {
                toast.error(formatApiError(err));
            }
        })();
    }, [open, prefillFromRentalId]);

    const rentalTemplates = templates.filter((t) => (t.template_type || "rental_agreement") === "rental_agreement" && !t.archived);
    const achTemplates = templates.filter((t) => t.template_type === "ach_authorization" && !t.archived);

    const updateRental = (k, v) => setRental((r) => ({ ...r, [k]: v }));

    // -------------------------------------------------------------
    // Phase AL1 equipment + Alvys lookup logic now lives in
    // ``<EquipmentSection>`` (Phase AE refactor). The dialog still
    // owns the ``rental`` state — EquipmentSection just calls
    // ``updateRental`` for the equipment subset of fields.
    // -------------------------------------------------------------

    // Phase H.6 — flipping toggle clears stale dropdown picks; flipping ON
    // re-applies the non-archived system default so the admin doesn't have to
    // re-pick every time they bounce the switch.
    const toggleBundleAch = (next) => {
        setBundleAch(next);
        if (!next) {
            setAchTemplateId("");
        } else if (achDefaultValid && achDefaultId) {
            setAchTemplateId(achDefaultId);
        } else {
            setAchTemplateId("");
        }
    };

    // Phase H.6 — client-side guard for the submit button. Backend remains
    // the source of truth (400s when bundle_ach=true and no template id/
    // default is resolvable).
    const achBlocksSubmit =
        bundleAch && !achTemplateId && !achDefaultValid;
    // Phase H.7 — additionally block submit when the equipment replacement
    // value is missing/<=0. Mirrors the backend's HTTP 400 guard so the
    // admin gets immediate feedback rather than a round-trip toast.
    const valueBlocksSubmit =
        !rental.value || (parseInt(String(rental.value).replace(/[^0-9]/g, ""), 10) || 0) <= 0;

    const submit = async () => {
        if (!title.trim()) { toast.error("Title is required"); return; }
        if (!templateId) { toast.error("Pick a Rental Agreement template"); return; }
        if (!rental.equipment_type) { toast.error("Equipment type is required"); return; }
        if (!rental.unit_number.trim()) { toast.error("Unit number is required"); return; }
        if (!rental.start_date) { toast.error("Start date is required"); return; }
        // Phase H.7 — equipment replacement value is required (>0).
        const _val = parseInt(String(rental.value || "0").replace(/[^0-9]/g, ""), 10) || 0;
        if (_val <= 0) { toast.error("Equipment replacement value is required"); return; }

        const rentalPayload = { ...rental };
        ["year", "daily_rate", "monthly_rate", "deposit", "minimum_term_months"].forEach((k) => {
            if (rentalPayload[k] === "" || rentalPayload[k] == null) delete rentalPayload[k];
            else rentalPayload[k] = Number(rentalPayload[k]);
        });
        // Phase H.7 — coerce value to int (already validated >0 above).
        rentalPayload.value = _val;
        // Phase AE — EquipmentSection now owns the Alvys lifecycle. The
        // ``alvys_trailer_id`` / ``alvys_synced_at`` fields are present
        // in ``rental`` IFF the admin clicked "Use this data" AND has
        // not subsequently dismissed the banner. The generic empty-
        // value cleanup below drops them when blank, so no explicit
        // delete branch is needed here.
        Object.keys(rentalPayload).forEach((k) => {
            if (rentalPayload[k] === "" || rentalPayload[k] == null) delete rentalPayload[k];
        });
        if (!rentalPayload.billing_period) delete rentalPayload.billing_period;
        // Phase TASK28E-REASSIGN-PREFILL — stamp the source-rental attribution
        // onto the outgoing rental subdoc so the new rental doc records its
        // lineage (visible in the rental's history + admin_action_audit).
        if (prefillFromRentalId) {
            rentalPayload.prefilled_from_rental_id = prefillFromRentalId;
        }

        const body = {
            type: "rental_agreement",
            title: title.trim(),
            template_id: templateId,
            notes: notes.trim() || undefined,
            rental: rentalPayload,
            // Phase H.6 — always send bundle_ach explicitly so server-side
            // logs/audit never see an ambiguous (missing) value from the UI.
            bundle_ach: bundleAch,
        };
        if (bundleAch && achTemplateId) body.ach_template_id = achTemplateId;

        setBusy(true);
        try {
            const { data } = await api.post(`/admin/customers/${customerId}/contracts`, body);
            toast.success("Contract created");
            onOpenChange(false);
            onCreated?.(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-rental-contract-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl">Create rental contract</DialogTitle>
                    <DialogDescription>
                        For {customerName || "this customer"}. Rental details and document templates — optionally bundle an ACH Authorization so the customer signs both in one session.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Phase TASK28E-REASSIGN-PREFILL — provenance banner */}
                    {prefillBanner && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50/70 px-4 py-3" data-testid="reassign-prefill-banner">
                            <div className="flex items-start gap-2">
                                <RefreshCcw size={16} className="text-blue-900 mt-0.5" />
                                <div className="text-sm">
                                    <p className="font-semibold text-blue-900">
                                        Prefilled from a previous rental
                                    </p>
                                    <p className="text-blue-900/80 mt-0.5">
                                        Source: <span className="font-mono">{prefillBanner.source_rental_id.slice(0, 8)}…</span> ·
                                        Customer <span className="font-medium">{prefillBanner.source_customer_name}</span> ·
                                        <span className="capitalize"> {prefillBanner.source_status}</span>. All fields are editable.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    {prefillError && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="reassign-prefill-error">
                            Prefill failed: {prefillError}. You can still create a rental manually.
                        </div>
                    )}
                    {/* Section A — rental details */}
                    <section className="space-y-3" data-testid="dlg-section-rental">
                        <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">A · Rental details</h3>
                        {/* Phase AE — equipment fields + Alvys lookup live in their own component now. */}
                        <EquipmentSection value={rental} onChange={updateRental} open={open} />
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Start date *</Label>
                                <Input type="date" value={rental.start_date} onChange={(e) => updateRental("start_date", e.target.value)} data-testid="rental-start" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>End date</Label>
                                <Input type="date" value={rental.end_date} onChange={(e) => updateRental("end_date", e.target.value)} data-testid="rental-end" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Daily rate ($)</Label>
                                <Input type="number" step="0.01" value={rental.daily_rate} onChange={(e) => updateRental("daily_rate", e.target.value)} data-testid="rental-daily" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Monthly rate ($)</Label>
                                <Input type="number" step="0.01" value={rental.monthly_rate} onChange={(e) => updateRental("monthly_rate", e.target.value)} data-testid="rental-monthly" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Minimum term (months)</Label>
                                <Input type="number" value={rental.minimum_term_months} onChange={(e) => updateRental("minimum_term_months", e.target.value)} data-testid="rental-min-term" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Deposit ($)</Label>
                                <Input type="number" step="0.01" value={rental.deposit} onChange={(e) => updateRental("deposit", e.target.value)} data-testid="rental-deposit" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>
                                    Equipment replacement value <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="$0"
                                    value={_displayValue(rental.value)}
                                    onChange={(e) => updateRental("value", _parseValueInput(e.target.value))}
                                    data-testid="rental-value"
                                    aria-required="true"
                                />
                                <p className="text-[11px] text-slate-500">Whole USD. Auto-fills the COI Physical Damage Limit.</p>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Billing period</Label>
                                <select value={rental.billing_period} onChange={(e) => updateRental("billing_period", e.target.value)}
                                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="rental-billing-period">
                                    <option value="">— auto —</option>
                                    <option value="daily">Daily</option>
                                    <option value="monthly">Monthly</option>
                                </select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Rental notes</Label>
                            <textarea value={rental.notes} onChange={(e) => updateRental("notes", e.target.value)} rows={2}
                                className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="rental-notes" />
                        </div>
                    </section>

                    {/* Section B — rental template */}
                    <section className="space-y-2" data-testid="dlg-section-template">
                        <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">B · Rental Agreement template *</h3>
                        <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                            className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="rental-template-select">
                            <option value="">— pick a template —</option>
                            {rentalTemplates.map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                        {defaultArchived && (
                            <p className="text-xs text-amber-700" data-testid="rental-template-default-archived-note">
                                Default template was archived — please choose one.
                            </p>
                        )}
                        {rentalTemplates.length === 0 && (
                            <p className="text-xs text-amber-700">No rental templates yet. Create one in the Templates page first.</p>
                        )}
                    </section>

                    {/* Section C — optional ACH (Phase H.6 — now gated by an explicit toggle) */}
                    <section className="space-y-3" data-testid="dlg-section-ach">
                        <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">
                            {bundleAch ? "C · ACH Authorization template" : "C · ACH Authorization"}
                        </h3>
                        <div className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
                            <Switch
                                checked={bundleAch}
                                onCheckedChange={toggleBundleAch}
                                data-testid="rental-ach-bundle-toggle"
                                aria-label="Include ACH Authorization"
                                className="mt-0.5"
                            />
                            <div className="space-y-0.5">
                                <p className="text-sm font-medium text-slate-800">Include ACH Authorization</p>
                                <p className="text-xs text-slate-500">
                                    A second document will be bundled for signing. Bank info is captured at signing time.
                                </p>
                            </div>
                        </div>
                        {bundleAch && (
                            <>
                                <select value={achTemplateId} onChange={(e) => setAchTemplateId(e.target.value)}
                                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="rental-ach-template-select">
                                    <option value="">— None —</option>
                                    {achTemplates.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                                {achDefaultArchived && (
                                    <p className="text-xs text-amber-700" data-testid="rental-ach-template-default-archived-note">
                                        Default template was archived — please choose one.
                                    </p>
                                )}
                                {achBlocksSubmit && (
                                    <p className="text-xs text-amber-700" data-testid="rental-ach-template-required-note">
                                        Pick an ACH template or turn off Include ACH Authorization.
                                    </p>
                                )}
                            </>
                        )}
                    </section>

                    {/* Section D — title + notes */}
                    <section className="space-y-3" data-testid="dlg-section-title">
                        <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">D · Title &amp; admin notes</h3>
                        <div className="space-y-1.5">
                            <Label>Title *</Label>
                            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Reefer rental — Q3 2026" data-testid="contract-title" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Internal notes</Label>
                            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                                className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="contract-notes" />
                        </div>
                    </section>
                </div>

                <DialogFooter className="mt-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="create-rental-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={busy || achBlocksSubmit || valueBlocksSubmit} className="bg-blue-900 hover:bg-blue-800" data-testid="create-rental-submit"
                        title={
                            achBlocksSubmit ? "Choose an ACH template or turn off Include ACH"
                            : valueBlocksSubmit ? "Equipment replacement value is required"
                            : undefined
                        }>
                        {busy ? "Creating…" : "Create contract"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CreateRentalContractDialog;
